<template>
  <div v-if="visible" class="stripe-verification-banner" role="status" aria-live="polite">
    <VaIcon :name="iconName" size="18px" class="stripe-verification-banner__icon" />
    <p class="stripe-verification-banner__text">
      {{ message }}
    </p>
    <RouterLink :to="{ name: 'complete-stripe-setup' }" class="stripe-verification-banner__link">
      {{ linkLabel }}
    </RouterLink>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { authApi } from '../../api/auth.api'
import { useAuthStore } from '../../stores/auth-store'

const route = useRoute()
const authStore = useAuthStore()

const visible = ref(false)
const needsAction = ref(false)
const awaitingConnect = ref(false)
let loadSeq = 0

const message = computed(() => {
  if (awaitingConnect.value) {
    return 'Connect Stripe when you are ready to receive tips. You can explore your profile and settings in the meantime.'
  }
  return needsAction.value
    ? 'Complete your Stripe payout setup to receive tips. You can still configure your HiTip account.'
    : 'Stripe is verifying your account. This may take a few minutes. You can set up your team and profile in the meantime.'
})

const linkLabel = computed(() => (awaitingConnect.value ? 'Connect Stripe' : 'Stripe setup'))

const iconName = computed(() => (awaitingConnect.value ? 'account_balance_wallet' : 'hourglass_top'))

async function loadStatus() {
  const seq = ++loadSeq
  const user = authStore.user
  if (!user) {
    visible.value = false
    awaitingConnect.value = false
    return
  }

  if (!user.stripe_connect_account_id) {
    if (seq !== loadSeq) return
    awaitingConnect.value = true
    needsAction.value = true
    visible.value = route.name !== 'complete-stripe-setup'
    return
  }

  awaitingConnect.value = false

  try {
    const status = await authApi.getConnectStatus()
    if (seq !== loadSeq) return
    if (authStore.user) {
      authStore.user = {
        ...authStore.user,
        stripe_connect_onboarding_complete: status.stripe_verified || status.onboarding_complete,
      }
    }
    const due = (status.requirements_currently_due?.length ?? 0) > 0 || (status.requirements_past_due?.length ?? 0) > 0
    needsAction.value = due
    visible.value = status.has_account && status.verification_pending
  } catch {
    if (seq !== loadSeq) return
    visible.value = false
  }
}

function scheduleLoadStatus() {
  void nextTick(() => {
    void loadStatus()
  })
}

watch(
  () => route.fullPath,
  () => {
    scheduleLoadStatus()
  },
  { immediate: true },
)
</script>

<style lang="scss" scoped>
.stripe-verification-banner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  padding: 10px 14px;
  margin: 30px 16px 0;
  border-radius: 12px;
  border: 1px solid rgba(245, 158, 11, 0.35);
  background: linear-gradient(90deg, rgba(245, 158, 11, 0.12), rgba(245, 158, 11, 0.04));

  @media (max-width: 600px) {
    margin: 15px 8px 0;
  }

  &__icon {
    flex-shrink: 0;
    color: #d97706;
  }

  &__text {
    flex: 1;
    min-width: 200px;
    margin: 0;
    font-size: 14px;
    line-height: 1.45;
    color: #78350f;
  }

  &__link {
    flex-shrink: 0;
    font-size: 13px;
    font-weight: 600;
    color: #b45309;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
